import {
  HamletClientConstructorType,
  HamletClientInterface,
  HamletClientOptionsType,
  HamletPricingRequestType,
  HamletPricingResponseType,
  ResendVerificationEmailRequestType,
  ResendVerificationEmailResponseType
} from '../../model/check-email-page';

const DEFAULT_BASE_URL =
  'https://wac.stg.internal.atlassian.com//apis/stg/hamlet';

/**
 * This class is extremely similar to the Hamlet client within the asc-cofs package. This class was created here,
 * rather than adding the appropriate functionality to the asc-cofs package itself, just because this was the path of
 * least resistance when creating this bxpkit package.
 */
export const HamletClient: HamletClientConstructorType = class HamletClientClass
  implements HamletClientInterface
{
  private readonly baseUrl: string;

  constructor({ baseUrl = DEFAULT_BASE_URL }: HamletClientOptionsType = {}) {
    this.baseUrl = baseUrl;
  }

  async hamletPricing({
    productKeys,
    licenseType = 'evaluation',
    requestingApplication = 'WAC',
    monthsValid = '1'
  }: HamletPricingRequestType): Promise<HamletPricingResponseType> {
    const productKeysParam =
      productKeys.length > 0
        ? `&${productKeys.map((p) => `productKey=${p}`).join('&')}`
        : '';

    const url =
      `${this.baseUrl}/1.0/public/pricing/query?licenseType=${licenseType}` +
      `${productKeysParam}&requestingApplication=${requestingApplication}` +
      `&monthsValid=${monthsValid}` +
      `&cacheBuster=${new Date().getTime()}`;

    return await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
      .then((response) => response.json())
      .catch(() => {
        return { products: [] };
      });
  }

  /**
   * If pagePath exists, use /sendVerify, else use /verify
   * @param resendData - R.E.D Object
   */

  sendEmail(
    resendData: ResendVerificationEmailRequestType
  ): Promise<ResendVerificationEmailResponseType> {
    if (resendData?.pagePath) {
      return this.resendVerificationEmail(resendData);
    } else {
      return this.sendVerificationEmail(resendData);
    }
  }

  /**
   * Typical resend endpoint, used in most cases
   */

  async sendVerificationEmail({
    cloudName,
    email,
    primaryProduct
  }: ResendVerificationEmailRequestType): Promise<ResendVerificationEmailResponseType> {
    const url =
      `${this.baseUrl}/1.0/public/aid/verify?` +
      `email=${encodeURIComponent(email || '')}&` +
      `accountName=${encodeURIComponent(cloudName || '')}&` +
      `primaryProduct=${encodeURIComponent(primaryProduct || '')}`;

    return await fetch(url, { method: 'POST' })
      .then((response) => {
        return {
          success: response.ok,
          status: response.status
        };
      })
      .catch(() => {
        return {
          success: false,
          status: 400
        };
      });
  }

  /**
   * Resend endpoint used if pagePath exists, used for:
   * - when sitename is auto-generated (except for social login case) (pagePath = 'rename/site')
   * - Confluence account set up flow (pagePath = 'account/setup')
   */

  async resendVerificationEmail({
    cloudId,
    cloudName,
    email,
    pagePath,
    requestId
  }: ResendVerificationEmailRequestType): Promise<ResendVerificationEmailResponseType> {
    return await fetch(`${this.baseUrl}/1.0/public/aid/sendVerify`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        accountName: cloudName,
        cloudId,
        cloudName: cloudName ? encodeURIComponent(cloudName) : undefined,
        email,
        pagePath: pagePath ? encodeURIComponent(pagePath) : undefined,
        requestId
      })
    })
      .then((response) => {
        return {
          success: response.ok,
          status: response.status
        };
      })
      .catch(() => {
        return {
          success: false,
          status: 400
        };
      });
  }
};
